import { Box, Button, Divider, Flex, IconButton, RadioGroup, Text, useDisclosure, Tag } from '@chakra-ui/core'
import { CustomRadio } from 'components/CommonComponents/AudiencePublicationModal/AudienceOptionItem'
import BaseModal from 'components/CommonComponents/BaseModal'
import LocaleText from 'components/LocaleText'
import colors from 'constants/colors'
import React, { memo, useMemo, useState } from 'react'
import { BsArrowLeftShort } from 'react-icons/bs'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { BiTask } from 'react-icons/bi'
import { BsChatDots } from 'react-icons/bs'
import { RootState } from 'constants/interfaces'
import { TasksLinksModal } from './TasksLinksModal'
import { ChatsLinksModal } from './ChatsLinksModal'
import { KpisLinksModal } from './KpisLinksModal'
import { CampaignsLinksModal } from './CampaignsLinksModal'
import { getInitialRadioValue, splitString } from './utils'
import { SmartAvatar } from 'components/CommonComponents/SmartAvatar'
import { useLinkedChatPost } from 'components/Post/postHooks/useLinkedChatPost'
import { allTasksSelector } from 'redux/selectors/tasks'
import { ChartNoAxesCombined, Megaphone } from 'lucide-react'

const RADIO_VALUES = {
  TASK: '1',
  CHAT: '2',
  KPI: '3',
  CAMPAIGN: '4',
} as const

interface IProps {
  isOpen: boolean
  closeModal: () => void
  setAttachedLink: (value: string) => void
  attachedLink: string | null
}

interface RadioOptionParams {
  value: string
  icon: React.ComponentType
  labelText: string
  onClick: () => void
  selectedContent?: React.ReactNode
}

export const LinkAttachmentModal = memo(({ isOpen, closeModal, setAttachedLink, attachedLink }: IProps) => {
  const splitedAttachedLink = attachedLink ? splitString(attachedLink) : null

  const { isOpen: isTaskModalOpen, onOpen: onOpenTaskModal, onClose: onCloseTaskModal } = useDisclosure()
  const { isOpen: isChatModalOpen, onOpen: onOpenChatModal, onClose: onCloseChatModal } = useDisclosure()
  const { isOpen: isKpiModalOpen, onOpen: onOpenKpiModal, onClose: onCloseKpiModal } = useDisclosure()
  const { isOpen: isCampaignModalOpen, onOpen: onOpenCampaignModal, onClose: onCloseCampaignModal } = useDisclosure()

  const isRtl = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)
  const { permission } = useSelector((state: RootState) => state.config.config)
  const { dynamic_task_views, default_task_views } = useSelector((state: RootState) => state.config.retailConfig!)
  const { chats } = useSelector((state: RootState) => state.feed)
  const tasks = useSelector(allTasksSelector)
  const kpis = useSelector((state: RootState) => state.ranking.kpisList)
  const { campaigns } = useSelector((state: RootState) => state.campaigns)

  const [selectedTaskLink, setSelectedTaskLink] = useState<string | null>(
    splitedAttachedLink?.key === 'tasks' || splitedAttachedLink?.key === '' ? splitedAttachedLink.value : null
  )
  const [selectedChatLink, setSelectedChatLink] = useState<string | null>(
    splitedAttachedLink?.key === 'posts' ? splitedAttachedLink.value : null
  )
  const [selectedKpiLink, setSelectedKpiLink] = useState<string | null>(
    splitedAttachedLink?.key === 'kpis' ? splitedAttachedLink.value : null
  )
  const [selectedCampaignLink, setSelectedCampaignLink] = useState<string | null>(
    splitedAttachedLink?.key === 'campaigns' ? splitedAttachedLink.value : null
  )
  const [value, setValue] = useState(() =>
    getInitialRadioValue(selectedTaskLink, selectedChatLink, selectedKpiLink, selectedCampaignLink)
  )

  const { linkedChat } = useLinkedChatPost({
    ref_id: selectedChatLink ? `posts-${selectedChatLink}` : null,
  })

  const allTasks = useMemo(() => {
    const mutatedTasks = tasks.map((task) => {
      return {
        task_view_id: task.ref_id,
        task_view_name: task.title,
        literal: '',
      }
    })
    return [...(mutatedTasks ?? []), ...(dynamic_task_views ?? []), ...(default_task_views ?? [])]
  }, [default_task_views, dynamic_task_views, tasks])

  const selectedTaskLinkTitle = useMemo(() => {
    if (selectedTaskLink) {
      const task = allTasks.find((t) => t.task_view_id == selectedTaskLink)
      return task?.task_view_name || task?.literal
    }
    return ''
  }, [allTasks, selectedTaskLink])

  const selectedChatLinkDetails = useMemo(() => {
    if (selectedChatLink && linkedChat) {
      return { title: linkedChat?.title, avatar: linkedChat?.images?.[0] }
    }
    return null
  }, [linkedChat, selectedChatLink])

  const selectedKpiLinkDetails = useMemo(() => {
    if (selectedKpiLink) {
      const kpi = kpis.find((kpi) => kpi.id === selectedKpiLink)
      return kpi?.title || ''
    }
    return ''
  }, [kpis, selectedKpiLink])

  const selectedCampaignLinkDetails = useMemo(() => {
    if (selectedCampaignLink) {
      const campaign = campaigns.find((c) => c.oid === selectedCampaignLink)
      return campaign?.title || ''
    }
    return ''
  }, [campaigns, selectedCampaignLink])

  const createRadioOption = ({ value: radioValue, icon, labelText, onClick, selectedContent }: RadioOptionParams) => {
    const isSelected = value === radioValue

    return (
      <>
        <CustomRadio
          isChecked={isSelected}
          w="100%"
          justifyContent="space-between"
          flexDir="row-reverse"
          value={radioValue}
          size="lg"
          color={theme?.elementsColor}
          onClick={onClick}
        >
          <Flex flexDir="row" alignItems="center" color={colors.blackLight}>
            <Box as={icon} h="48px" w="48px" p="10px" borderRadius="full" bg={colors.greyLight4} />
            <Flex flexDir="column">
              <Text mx="10px">
                <LocaleText text={labelText} />
              </Text>
              {selectedContent}
            </Flex>
          </Flex>
        </CustomRadio>
        <Divider pt="5px" />
      </>
    )
  }

  const createSelectionTag = (text: string) => (
    <Tag
      mx="10px"
      w="max-content"
      background={colors.tealLight}
      fontWeight="bold"
      border="1px solid"
      fontSize="14px"
      maxWidth="300px"
    >
      <Text isTruncated>
        <LocaleText text={text} />
      </Text>
    </Tag>
  )

  const linkHandlers: Record<string, () => void> = {
    [RADIO_VALUES.TASK]: () => {
      if (selectedTaskLink) {
        const selectedTaskInTasks = tasks.some((task) => task.ref_id == selectedTaskLink)
        setAttachedLink(selectedTaskInTasks ? selectedTaskLink : `tasks-${selectedTaskLink}`)
      }
    },
    [RADIO_VALUES.CHAT]: () => {
      if (selectedChatLink) {
        setAttachedLink(`posts-${selectedChatLink}`)
      }
    },
    [RADIO_VALUES.KPI]: () => {
      if (selectedKpiLink) {
        setAttachedLink(`kpis-${selectedKpiLink}`)
      }
    },
    [RADIO_VALUES.CAMPAIGN]: () => {
      if (selectedCampaignLink) {
        setAttachedLink(`campaigns-${selectedCampaignLink}`)
      }
    },
  }

  const handleSubmit = () => {
    const handler = linkHandlers[value!]
    if (handler) {
      handler()
    } else {
      setAttachedLink('')
    }
    closeModal()
  }

  return (
    <>
      <BaseModal
        display={isTaskModalOpen || isChatModalOpen ? 'none' : 'block'}
        isShowAnimation={true}
        isOpen={isOpen}
        onClose={closeModal}
        hideCloseButton={true}
        headerContent={
          <>
            <Flex alignItems="center" justifyContent="space-between" dir={isRtl ? 'rtl' : 'ltr'}>
              <Box onClick={closeModal} mx="4" position="absolute">
                <IconButton
                  aria-label="close groups"
                  icon={BsArrowLeftShort}
                  borderRadius="50%"
                  style={{ color: colors.greyMain }}
                  fontSize="32px"
                  transform={`rotate(${+isRtl * 180}deg)`}
                />
              </Box>

              <Text mx="10px" textAlign="center" flexGrow={1}>
                <LocaleText text="Choose attachment" />
              </Text>
            </Flex>

            <Divider pt="5px" />
          </>
        }
        bodyContent={
          <RadioGroup value={value ?? ''} px="25px" mt="20px">
            {createRadioOption({
              value: RADIO_VALUES.TASK,
              icon: BiTask,
              labelText: 'link_task',
              onClick: onOpenTaskModal,
              selectedContent: selectedTaskLinkTitle ? createSelectionTag(selectedTaskLinkTitle) : undefined,
            })}

            {createRadioOption({
              value: RADIO_VALUES.CHAT,
              icon: BsChatDots,
              labelText: 'link_chat',
              onClick: onOpenChatModal,
              selectedContent: selectedChatLinkDetails ? (
                <Tag
                  mx="10px"
                  maxWidth="300px"
                  fontSize="12px"
                  w="max-content"
                  background={colors.tealLight}
                  border="1px solid"
                >
                  <Flex
                    flexDir="row"
                    alignItems="center"
                    color={colors.blackLight}
                    p="5px"
                    fontWeight="bold"
                    fontSize="14px"
                  >
                    <SmartAvatar
                      image={selectedChatLinkDetails.avatar}
                      size="40px"
                      backgroundColor={colors.greyLight4}
                      iconColor={colors.tealLight}
                    />
                    <Text isTruncated mx="10px">
                      {selectedChatLinkDetails.title}
                    </Text>
                  </Flex>
                </Tag>
              ) : undefined,
            })}

            {kpis.length > 0 &&
              permission?.users === 3 &&
              createRadioOption({
                value: RADIO_VALUES.KPI,
                icon: ChartNoAxesCombined,
                labelText: 'link_kpi',
                onClick: onOpenKpiModal,
                selectedContent: selectedKpiLinkDetails ? createSelectionTag(selectedKpiLinkDetails) : undefined,
              })}

            {campaigns.length > 0 &&
              permission?.users === 3 &&
              createRadioOption({
                value: RADIO_VALUES.CAMPAIGN,
                icon: Megaphone,
                labelText: 'link_campaign',
                onClick: onOpenCampaignModal,
                selectedContent: selectedCampaignLinkDetails
                  ? createSelectionTag(selectedCampaignLinkDetails)
                  : undefined,
              })}
          </RadioGroup>
        }
        footerContent={
          <>
            <Flex justifyContent="flex-end" background="white" px="6" py={5}>
              <Button
                borderRadius="20px"
                onClick={handleSubmit}
                color="white"
                textTransform="uppercase"
                background={theme?.elementsColor}
                _hover={{ backgroundColor: 'none' }}
              >
                <LocaleText text="post_audience_done" />
              </Button>
            </Flex>
          </>
        }
      />
      {isTaskModalOpen && (
        <TasksLinksModal
          isOpen={isTaskModalOpen}
          closeModal={onCloseTaskModal}
          allTasks={allTasks}
          selectedTaskLink={selectedTaskLink}
          setSelectedTaskLink={(val) => {
            setSelectedTaskLink(val)
            setValue(RADIO_VALUES.TASK)
          }}
          setSelectedChatLink={setSelectedChatLink}
          setSelectedKpiLink={setSelectedKpiLink}
        />
      )}
      {isChatModalOpen && (
        <ChatsLinksModal
          isOpen={isChatModalOpen}
          closeModal={onCloseChatModal}
          selectedChatLink={selectedChatLink}
          setSelectedChatLink={(val) => {
            setSelectedChatLink(val)
            setValue(RADIO_VALUES.CHAT)
          }}
          setSelectedTaskLink={setSelectedTaskLink}
          setSelectedKpiLink={setSelectedKpiLink}
          chats={chats}
        />
      )}
      {isKpiModalOpen && (
        <KpisLinksModal
          isOpen={isKpiModalOpen}
          closeModal={onCloseKpiModal}
          selectedKpiLink={selectedKpiLink}
          setSelectedKpiLink={(val) => {
            setSelectedKpiLink(val)
            setValue(RADIO_VALUES.KPI)
          }}
          setSelectedTaskLink={setSelectedTaskLink}
          setSelectedChatLink={setSelectedChatLink}
          kpis={kpis}
        />
      )}
      {isCampaignModalOpen && (
        <CampaignsLinksModal
          isOpen={isCampaignModalOpen}
          closeModal={onCloseCampaignModal}
          selectedCampaignLink={selectedCampaignLink}
          setSelectedCampaignLink={(val: string) => {
            setSelectedCampaignLink(val)
            setValue(RADIO_VALUES.CAMPAIGN)
          }}
          setSelectedTaskLink={setSelectedTaskLink}
          setSelectedChatLink={setSelectedChatLink}
          setSelectedKpiLink={setSelectedKpiLink}
          campaigns={campaigns}
        />
      )}
    </>
  )
})
